import { computed, reactive } from 'vue'
import { normalizeFormErrorMessage } from '@/common/utils/normalizeFormErrorMessage'

/** Form-level error text by pagesData slot (en.setFormError / UniData.formError). */
const formErrorBySlot = reactive(new Map<string, string>())

function slotKey(slotId: string | number | null | undefined): string {
  return String(slotId ?? '').trim()
}

function readFormErrorField(payload: Record<string, unknown>): { present: boolean; raw: unknown } {
  if (Object.prototype.hasOwnProperty.call(payload, 'formError')) {
    return { present: true, raw: payload.formError }
  }
  const jsResult = payload.jsResult
  if (jsResult && typeof jsResult === 'object' && !Array.isArray(jsResult)) {
    const jr = jsResult as Record<string, unknown>
    if (Object.prototype.hasOwnProperty.call(jr, 'formError')) {
      return { present: true, raw: jr.formError }
    }
  }
  const uniData = payload.uniData
  if (uniData && typeof uniData === 'object' && !Array.isArray(uniData)) {
    const ud = uniData as Record<string, unknown>
    if (Object.prototype.hasOwnProperty.call(ud, 'formError')) {
      return { present: true, raw: ud.formError }
    }
  }
  return { present: false, raw: undefined }
}

export function getFormError(slotId: string | number | null | undefined): string | null {
  const key = slotKey(slotId)
  if (!key) return null
  return formErrorBySlot.get(key) ?? null
}

export function setFormErrorForSlot(
  slotId: string | number | null | undefined,
  message: unknown,
): void {
  const key = slotKey(slotId)
  if (!key) return
  const normalized = normalizeFormErrorMessage(message)
  if (normalized == null) {
    formErrorBySlot.delete(key)
    return
  }
  formErrorBySlot.set(key, normalized)
}

export function clearFormError(slotId: string | number | null | undefined): void {
  setFormErrorForSlot(slotId, null)
}

/** Reactive form error for a page slot (FormErrorBanner). */
export function useFormError(slotId: () => string | number | null | undefined) {
  return computed(() => {
    const key = slotKey(slotId())
    if (!key) return null as string | null
    void formErrorBySlot.size
    return formErrorBySlot.get(key) ?? null
  })
}

/** Normalized formError when payload exposes the field; null if absent or empty. */
export function extractFormErrorFromPayload(payload: unknown): string | null {
  if (payload == null || typeof payload !== 'object' || Array.isArray(payload)) {
    return null
  }
  const { present, raw } = readFormErrorField(payload as Record<string, unknown>)
  if (!present) return null
  return normalizeFormErrorMessage(raw)
}

/**
 * Apply formError from response when the field is present (including clear on empty).
 * Returns true when payload had a formError key to apply.
 */
export function applyFormErrorFromPayload(
  slotId: string | number | null | undefined,
  payload: unknown,
): boolean {
  if (payload == null || typeof payload !== 'object' || Array.isArray(payload)) {
    return false
  }
  const { present, raw } = readFormErrorField(payload as Record<string, unknown>)
  if (!present) return false
  setFormErrorForSlot(slotId, raw)
  return true
}
